import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Search, Link2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface DocumentLinkerProps {
  dealId: string;
  /** Primary contact email (deal contact) */
  dealEmail?: string;
  /** Emails of linked companies (Cégek) – documents matching any of these will be shown and linkable */
  companyEmails?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SuperfakturaDocument {
  id: string;
  name?: string;
  type?: string;
  number?: string;
  total?: number;
  date?: string;
  email?: string;
  Invoice?: { name?: string; invoice_no?: string; total?: number; created?: string; date?: string; type?: string };
  Quote?: { name?: string; invoice_no?: string; total?: number; created?: string; date?: string; type?: string };
  [key: string]: any;
}

/** Normalize SF API document (often nested under Invoice/Quote) for display */
function docDisplay(doc: SuperfakturaDocument): { name: string; number?: string; total?: number; date?: string; type?: string } {
  const inv = doc.Invoice ?? doc.invoice;
  const quo = doc.Quote ?? doc.quote;
  const name =
    doc.name ??
    inv?.name ??
    quo?.name ??
    inv?.invoice_no ??
    quo?.invoice_no ??
    String(doc.id);
  const number = doc.number ?? inv?.invoice_no ?? quo?.invoice_no ?? inv?.number ?? quo?.number;
  const total = doc.total ?? inv?.total ?? quo?.total;
  const date = doc.date ?? doc.created ?? inv?.date ?? inv?.created ?? quo?.date ?? quo?.created;
  const type = doc.type ?? inv?.type ?? quo?.type;
  return { name, number, total, date, type };
}

const documentTypes = [
  { value: "quote", labelKey: "documentLinker.typeQuote" },
  { value: "invoice", labelKey: "documentLinker.typeInvoice" },
  { value: "deposit_invoice", labelKey: "documentLinker.typeDepositInvoice" },
  { value: "proforma", labelKey: "documentLinker.typeProforma" },
  { value: "credit_note", labelKey: "documentLinker.typeCreditNote" },
  { value: "expense", labelKey: "documentLinker.typeExpense" },
];

export function DocumentLinker({
  dealId,
  dealEmail,
  companyEmails = [],
  isOpen,
  onClose,
  onSuccess,
}: DocumentLinkerProps) {
  const { t } = useTranslation();
  const [searchEmail, setSearchEmail] = useState(dealEmail || "");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const qc = useQueryClient();

  // When modal opens, pre-fill email with contact email
  useEffect(() => {
    if (isOpen && dealEmail && dealEmail.length >= 2) {
      setSearchEmail(dealEmail);
    }
  }, [isOpen, dealEmail]);

  // All emails to search: contact + companies + optional extra from input (dedupe)
  const allEmails = useMemo(() => {
    const list = [
      dealEmail,
      ...companyEmails,
      searchEmail.length >= 2 ? searchEmail : null,
    ].filter((e): e is string => !!e && String(e).trim().length >= 2);
    const normalized = list.map((e) => String(e).trim().toLowerCase());
    return [...new Set(normalized)];
  }, [dealEmail, companyEmails, searchEmail]);

  // Fetch documents by all relevant emails and merge by id
  const emailDocuments = useQuery<SuperfakturaDocument[], Error>({
    queryKey: ["sf-documents-emails", dealId, allEmails.sort().join("|")],
    queryFn: async () => {
      if (allEmails.length === 0) return [];
      const results = await Promise.all(
        allEmails.map((email) =>
          fetch(apiUrl(`/api/superfaktura/${encodeURIComponent(email)}`), { credentials: "include" })
            .then((r) => (r.ok ? r.json() : { documents: [] }))
            .then((d) => (d.documents || []) as SuperfakturaDocument[])
        )
      );
      const byId = new Map<string, SuperfakturaDocument>();
      for (const list of results) {
        for (const doc of list) {
          const id = doc.id ?? (doc as any).Invoice?.id ?? (doc as any).Quote?.id;
          if (id && !byId.has(String(id))) byId.set(String(id), doc);
        }
      }
      return Array.from(byId.values());
    },
    enabled: isOpen && allEmails.length >= 1,
  });

  const searchDocuments = useQuery<SuperfakturaDocument[], Error>({
    queryKey: ["sf-documents-search", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const r = await fetch(apiUrl(`/api/superfaktura/search/all?q=${encodeURIComponent(searchQuery)}`), { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      return (data.documents || []) as SuperfakturaDocument[];
    },
    enabled: isOpen && !!searchQuery && searchQuery.length >= 2,
  });

  const typeDocuments = useQuery<SuperfakturaDocument[], Error>({
    queryKey: ["sf-documents-type", selectedType],
    queryFn: async () => {
      if (!selectedType || selectedType === "all") return [];
      const r = await fetch(apiUrl(`/api/superfaktura/documents/${selectedType}`), { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      return (data.documents || []) as SuperfakturaDocument[];
    },
    enabled: isOpen && !!selectedType && selectedType !== "all",
  });

  const linkDocument = useMutation({
    mutationFn: async (doc: SuperfakturaDocument) => {
      const d = docDisplay(doc);
      const url = apiUrl(`/api/crm/deals/${dealId}/documents`);
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          sfDocumentId: doc.id,
          documentType: doc.type,
          documentNumber: d.number ?? d.name,
          totalAmount: doc.total,
          documentDate: doc.date,
          rawData: doc,
        }),
      });
      if (!r.ok) {
        const errorText = await r.text().catch(() => "Unknown error");
        try {
          const error = JSON.parse(errorText);
          throw new Error(error.message || error.error || "Linkelés sikertelen");
        } catch (e) {
          if (e instanceof Error) throw e;
          throw new Error(errorText || "Linkelés sikertelen");
        }
      }
      return r.json();
    },
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("documentLinker.documentLinkedSuccess"),
      });
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      onSuccess?.();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Determine which documents to show
  let displayDocuments: SuperfakturaDocument[] = [];
  let isLoading = false;

  if (searchQuery.length >= 2) {
    displayDocuments = searchDocuments.data || [];
    isLoading = searchDocuments.isLoading;
  } else if (selectedType && selectedType !== "all") {
    displayDocuments = typeDocuments.data || [];
    isLoading = typeDocuments.isLoading;
  } else if (allEmails.length >= 1) {
    displayDocuments = emailDocuments.data || [];
    isLoading = emailDocuments.isLoading;
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t("documentLinker.linkTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Search by Email (contact + linked companies; add more in field to include) */}
          <div>
            <label className="block text-sm font-medium mb-2">{t("documentLinker.searchByEmail")}</label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t("documentLinker.emailPlaceholder")}
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
              />
              <Button
                variant="outline"
                onClick={() => emailDocuments.refetch()}
                disabled={emailDocuments.isLoading || allEmails.length === 0}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {allEmails.length > 1 && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("documentLinker.searchingContactAndCompanies", "Keresés a kapcsolat és a linkelt cégek e-mailjei alapján.")}
              </p>
            )}
          </div>

          {/* Search by Type */}
          <div>
            <label className="block text-sm font-medium mb-2">{t("documentLinker.searchByType")}</label>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger>
                <SelectValue placeholder={t("documentLinker.selectTypePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("documents.allTypes")}</SelectItem>
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {t(type.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search by Query */}
          <div>
            <label className="block text-sm font-medium mb-2">{t("documentLinker.searchByText")}</label>
            <Input
              placeholder={t("common.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Results */}
          {isLoading && (
            <div className="text-center py-8 text-foreground/60">{t("documentLinker.searching")}</div>
          )}

          {!isLoading && displayDocuments.length === 0 && (
            <div className="text-center py-8 text-foreground/60">
              {t("documentLinker.noResults")}
            </div>
          )}

          {!isLoading && displayDocuments.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {displayDocuments.map((doc) => {
                const d = docDisplay(doc);
                return (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{d.name || String(doc.id)}</div>
                      <div className="text-sm text-foreground/60 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {d.type && (
                          <Badge variant="outline" className="shrink-0">{d.type}</Badge>
                        )}
                        {d.number && <span>{d.number}</span>}
                        {d.total != null && <span>{Number(d.total).toFixed(2)} EUR</span>}
                        {d.date && (
                          <span>• {new Date(d.date).toLocaleDateString("hu-HU")}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => linkDocument.mutate(doc)}
                      disabled={linkDocument.isPending}
                      className="ml-2 shrink-0"
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Linkelés
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


